'use strict';

/**
 * @module views/markers/freefloater
 * @author Philip Bräunlich
 */

const MARKER_SIZE = 10;
const MARKER_SPACE = 2;
const MARKER_SELECTED_BORDER_WIDTH = 3;

const Marker = require('./_');

const $ = require('zepto');
const EventHelper = require('../../helpers/event');
const throttle = require('lodash.throttle');

const ConfigurationHelper = require('../../helpers/configuration');

const FreefloaterInfowindowTemplate = require('../../../templates/freefloaterInfowindow.html');
const TemplateHelper = require('../../helpers/template');
const DateHelper = require('../../helpers/data');

class FreefloaterMarker extends Marker {
	constructor (place, map) {
		super({
			type: 'freefloater',
			object: place,
			map
		});
		
		this.meta = {
			bookingButtonLabel: ConfigurationHelper.getString('freefloater.bookingButtonLabel'),
			bookingUrl: ConfigurationHelper.getBookingUrl({bookee: place.bookees[0], place: place})
		}
		
		this.overlayView = {
			$el: undefined,
			parent: {
				$el: $('.map__info')
			}
		};

		google.maps.event.addListener(this.marker, 'click', () => {
			if (ConfigurationHelper.openMarkerDirectly() === true) {
				window.open(this.meta.bookingUrl, 'ewi3');
				return;
			}
			
			if (this.selected()) {
				EventHelper.unselectPlace(place.id);
			} else {
				EventHelper.selectPlace({
					placeId: place.id,
					options: {
						single: true,
						panTo: true
					}
				});
			}
		});
		
		EventHelper.onPlaceSelect(this, this.handlePlaceSelect);
	}
	
	isNear(place, center) {
		const zoom = this.map.map.getZoom()
		if (zoom <= 13) { return false; }
		
		const distanceInMeters = Math.max((35 - Math.abs(zoom - 14) * 7.5), 5) || 35;
		
		const ky = 40000 / 360;
		const kx = Math.cos(Math.PI * center.latitude / 180.0) * ky;
		const dx = Math.abs(center.longitude - place.longitude) * kx;
		const dy = Math.abs(center.latitude - place.latitude) * ky;
		return Math.sqrt(dx * dx + dy * dy) * 1000 <= distanceInMeters;
	}
	
	handlePlaceSelect (data) {
		if (data.places.includes(this.object.id)) {
			this.selected(true, data.options.panTo);
		} else {
			this.selected(false);
		}
	}

	renderMarker () {
		const icon = {};
		
		if (ConfigurationHelper.getCustomMarkerConfig('freeFloater')) {
			const customMarkerConf = ConfigurationHelper.getCustomMarkerConfig('freeFloater');
			icon.size = new google.maps.Size(customMarkerConf.size[0], customMarkerConf.size[1]);
			icon.scaledSize = new google.maps.Size(customMarkerConf.scaledSize[0], customMarkerConf.scaledSize[1]);
			icon.anchor = new google.maps.Point(customMarkerConf.anchor[0], customMarkerConf.anchor[1]);
			icon.url = FreefloaterMarker.getFreefloaterMarkerUrl(!!this.object.selected);
		} else {
			icon.anchor = new google.maps.Point(MARKER_SIZE * MARKER_SPACE, MARKER_SIZE * MARKER_SPACE),
			icon.origin = new google.maps.Point((MARKER_SIZE * MARKER_SPACE * 2 - MARKER_SIZE * MARKER_SPACE) / -2, (MARKER_SIZE * MARKER_SPACE * 2 - MARKER_SIZE * MARKER_SPACE) / -2), // -10
			icon.size = new google.maps.Size(MARKER_SIZE * MARKER_SPACE * 2, MARKER_SIZE * MARKER_SPACE * 2),
			icon.scaledSize = new google.maps.Size(MARKER_SIZE * MARKER_SPACE, MARKER_SIZE * MARKER_SPACE),
			icon.url = FreefloaterMarker.getFreefloaterMarkerUrl(!!this.object.selected)
		}

		return new google.maps.Marker({
			clickable: true,
			zIndex: 10,
			cursor: 'pointer',
			icon,
			map: this.map.map,
			optimized: false,
			infoWindow: null,
			position: new google.maps.LatLng(this.object.geoCoordinate.latitude, this.object.geoCoordinate.longitude),
			title: this.object.name
		});
	}

	selected (value, panToPlace) {
		if (value === undefined) {
			return this._selected || false;
		}
		
		if (this._selected == !!value) { return; }

		this._selected = !!value;
		this.marker.setIcon(Object.assign({}, this.marker.getIcon(), {
			url: FreefloaterMarker.getFreefloaterMarkerUrl(this.selected())
		}));

		if(panToPlace && this._selected) {
			const currentZoom = this.map.map.getZoom();
			this.map.panTo(this.marker.getPosition(), currentZoom < 15 ? 15 : currentZoom);
		}
		
		this.toggleInfoWindow(this._selected);
	}

	static getFreefloaterMarkerUrl (selected) {
		if (selected && ConfigurationHelper.getCustomMarkerConfig('freeFloater-selected')) {
			return ConfigurationHelper.getCustomMarkerConfig('freeFloater-selected').file;
		}
		if (ConfigurationHelper.getCustomMarkerConfig('freeFloater')) {
			return ConfigurationHelper.getCustomMarkerConfig('freeFloater').file;
		}
		if (FreefloaterMarker.markerCache && FreefloaterMarker.markerCache[selected]) {
			return FreefloaterMarker.markerCache[selected];
		}

		const canvas = document.createElement('canvas');
		canvas.width = MARKER_SIZE * MARKER_SPACE * 3;
		canvas.height = MARKER_SIZE * MARKER_SPACE * 3;

		const radius = MARKER_SIZE * 3 / 2;
		const length = radius * 2 * 1.1;
		let w = length;
		
		let k = (canvas.width - w) / 2;
		
		const context = canvas.getContext('2d');
		context.clearRect(0, 0, canvas.width, canvas.height);
	

		if (selected) {
			context.lineWidth = MARKER_SELECTED_BORDER_WIDTH * 2.5;
			context.strokeStyle = ConfigurationHelper.getFreefloaterColor();
			
			context.beginPath();
			context.moveTo(k + w / 2, k + w * .1);
			context.lineTo(k + 0, k + w);
			context.lineTo(k + w, k + w);
			context.closePath();
			context.stroke();
		}
		else {
			context.fillStyle = ConfigurationHelper.getFreefloaterColor();

			context.beginPath();
			context.moveTo(k + w / 2, k + w * .1);
			context.lineTo(k + 0, k + w);
			context.lineTo(k + w, k + w);
			context.closePath();
			
			context.fill();
		}

		const url = canvas.toDataURL('image/png');
		FreefloaterMarker.markerCache = FreefloaterMarker.markerCache || {};
		FreefloaterMarker.markerCache[selected] = url;
		return url;
	}
	
	async toggleInfoWindow(show) {
		const parentHasWindow = this.overlayView.parent.$el.find('[data-id="'+ this.object.id +'"]').length > 0;
		if (show === parentHasWindow) { return; }
		
		// view is there, remove it.
		if (!show) {
			this._removeInfoWindow();
			return;
		}
		
		
		google.maps.event.addListenerOnce(this.map.map, 'idle', async () => {
			const bookeeProduct = await DateHelper.request('getBookeeProductsByBookeeProductIds', {bookeeProductIds: [this.object.bookees[0].bookeeProductId]});
			
			TemplateHelper.render({
				view: this.overlayView,
				template: FreefloaterInfowindowTemplate,
				data: {
					place: this.object,
					bookeeProduct: bookeeProduct.length ? bookeeProduct[0] : null,
					meta: this.meta
				}
			});
			
			this.nearByListener();
			
			// register 
			this.overlayView.$el.find('.ffinfo__close').one('click', () => EventHelper.unselectPlace(this.object.id));
		});

	}
	
	_removeInfoWindow () {
		if (this.overlayView.$el) {
			this.overlayView.$el.remove();
		}
		delete this.overlayView.$el;
	}
	
	nearByListener () {
		this.mapListener = google.maps.event.addListener(this.map.map, 'bounds_changed', throttle(() => {
				const center = {
					latitude: this.map.map.getCenter().lat(),
					longitude: this.map.map.getCenter().lng(),
				};
		
				const isNearby = this.isNear(this.object.geoCoordinate, center);
				if (!isNearby) {
					EventHelper.unselectPlace(this.object.id);
					this.mapListener.remove();
				}
		}, 250));
	}
}

module.exports = FreefloaterMarker;
